// Public read API (anonymous), served same-origin via the BFF under /api/public.
// Mirrors the backend DTOs in Features/WeatherData/WeatherReadRegistration.cs.
import type { RatingQuality, SightingCategory, VisibilityBucket } from '@/api/ratings'

export type ConditionRating = 'Good' | 'Marginal' | 'Poor' | 'Unknown'

export interface WeatherNow {
  airTemperature: number | null
  windSpeed: number | null
  windFromDirection: number | null
  relativeHumidity: number | null
  airPressureAtSeaLevel: number | null
  cloudAreaFraction: number | null
}

export interface OceanNow {
  seaWaterTemperature: number | null
  seaWaterSpeed: number | null
  seaWaterToDirection: number | null
  seaSurfaceWaveHeight: number | null
  seaSurfaceWaveFromDirection: number | null
}

/** Den nyeste sigtmelding, kort nok til et listekort. */
export interface LatestReport {
  /** Styrer kuloeren; ordet kommer fra `category`. */
  quality: RatingQuality
  category: SightingCategory
  visibilityMeters: number
  /** Null paa meldinger fra foer ombygningen — de udloeber efter 48 timer. */
  visibilityBucket: VisibilityBucket | null
  wildlife: string[]
  observedAt: string
}

export interface DiveSiteConditions {
  id: string
  navn: string
  latitude: number
  longitude: number
  altitude: number | null
  seaDirection: number | null
  syncTime: string | null
  forecastUpdatedAt: string | null
  conditionRating: ConditionRating
  weather: WeatherNow | null
  ocean: OceanNow | null
  // Country og address kommer fra BAADE listen og detaljen — listekortet viser stedet.
  country?: string | null
  address?: string | null
  // Nyeste sigtmelding + hvor mange der er i alt (sidste 48 timer). Kun fra listen; paa detaljen
  // staar hele listen i RatingSection.
  latestReport?: LatestReport | null
  reportCount?: number
  // Descriptive info — only present on the detail endpoint (M10/M11).
  parking?: string | null
  facilities?: string | null
  toilet?: boolean
  parkering?: boolean
  wildlife?: string | null
  maxDepth?: number | null
  description?: string | null
  nearestAed?: string | null
  nearestAedLatitude?: number | null
  nearestAedLongitude?: number | null
  diveGuide?: string | null
  floraFauna?: string | null
  // Kortudsnit gemt af admin; er det sat, viser kortet praecis det udsnit.
  mapCenterLatitude?: number | null
  mapCenterLongitude?: number | null
  mapZoom?: number | null
  heroImageUrl?: string | null
}

export interface ForecastPoint {
  time: string
  airTemperature: number | null
  windSpeed: number | null
  windFromDirection: number | null
  seaWaterTemperature: number | null
  seaSurfaceWaveHeight: number | null
  conditionRating: ConditionRating
}

export interface Forecast {
  diveSiteId: string
  updatedAt: string | null
  expires: string | null
  points: ForecastPoint[] | null
}

// Understrøm fra Copernicus (M16) — kun for sites dybere end 10 m. Prøve ved 3 dybder (D/3, 2D/3, D).
/** Overflade og bund kommer fra FCOO og er ikke dybder — derfor et lag frem for et tal. */
export type CurrentLayer = 'surface' | 'bottom'

export interface CurrentSample {
  depthM: number
  uo: number | null
  vo: number | null
  speedMs: number | null
  directionDeg: number | null
  layer?: CurrentLayer | null
}

/** Én søjle i skemaet. `depthM` er null når kilden ikke oplyser dybden (FCOO's bund). */
export interface CurrentColumn {
  key: string
  layer: CurrentLayer | null
  depthM: number | null
}

export interface CurrentPoint {
  time: string
  samples: CurrentSample[]
}

export interface CurrentExtreme {
  time: string
  speedMs: number
  directionDeg: number
}

export interface CurrentSummary {
  strongest: CurrentExtreme | null
  weakest: CurrentExtreme | null
  /** Tidspunkter hvor strømmen vender. Tom når opløsningen er for grov til at se det. */
  turns: string[]
}

export interface CurrentRow {
  time: string
  samples: CurrentSample[]
}

/** Én dags skema: 2-timers rækker 06–20 dansk tid, plus dagens opsummering. */
export interface CurrentDay {
  date: string
  summary: CurrentSummary | null
  rows: CurrentRow[]
}

export interface CurrentForecast {
  diveSiteId: string
  syncTime: string | null
  forecastUpdatedAt: string | null
  depths: number[]
  points: CurrentPoint[]
  /** Hvilken model dataen kom fra, fx `dmi-dkss-idw`. */
  source: string | null
  /** Serieopløsning i minutter. 360 betyder at en vending ikke kan opløses. */
  stepMinutes: number | null
  days: CurrentDay[] | null
  /** Skemaets søjler i visningsrækkefølge. Afløser `depths`, som ikke kan bære overflade og bund. */
  columns: CurrentColumn[] | null
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`/api/public${path}`, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error(`GET /api/public${path} → ${res.status}`)
  return (await res.json()) as T
}

export interface DailyCondition {
  date: string
  rating: ConditionRating
  windSpeed: number | null
}

export const publicApi = {
  listSites: () => get<DiveSiteConditions[]>('/divesites'),
  getSite: (id: string) => get<DiveSiteConditions>(`/divesites/${id}`),
  getForecast: (id: string) => get<Forecast>(`/divesites/${id}/forecast`),
  getCurrent: (id: string) => get<CurrentForecast>(`/divesites/${id}/current`),
  getDailyHistory: (id: string) => get<DailyCondition[]>(`/divesites/${id}/history/daily`),
}
